import React from 'react';
import styled, { css } from 'styled-components';
import TinyColor from 'tinycolor2';

import { ProductCardButtonOption } from 'types/graphql';
import { EmbeddedExternalLink } from 'src/components/core';

import { useCardContext } from './card-context';

/** Add-to-cart button, or "View Item" link in embedded carousel mode. */
export function CardActionButton(): JSX.Element {
  const { isEmbeddedCarousel, href, buttonCopy, onAddToCart } = useCardContext();

  if (isEmbeddedCarousel) {
    return (
      <EmbeddedButton href={href} data-testid='card-button'>
        View Item
      </EmbeddedButton>
    );
  }

  return (
    <Button onClick={onAddToCart} data-testid='card-button'>
      {buttonCopy}
    </Button>
  );
}

const tintedButtonStyles = css`
  /* Hex alpha 10% */
  background: ${({ theme }) => theme.customized.colors.buttonsLinks}1a;
  color: ${({ theme }) => TinyColor.mix(theme.customized.colors.buttonsLinks, '#000', 20).toString()};
`;

const solidButtonStyles = css`
  background: ${({ theme }) => theme.customized.colors.buttonsLinks};
  color: #fff;
`;

const solidButtonHoverStyles = css`
  background: ${({ theme }) => TinyColor.mix(theme.customized.colors.buttonsLinks, '#000', 20).toString()};
  color: #fff;
`;

const buttonStyles = css`
  appearance: none;
  border: none;
  border-radius: ${({ theme }) => theme.customized.radius.buttons};
  cursor: pointer;
  display: block;
  font-size: 13px;
  font-weight: 600;
  line-height: ${20 / 13};
  padding: 8px;
  position: relative;
  text-align: center;
  text-transform: ${({ theme }) => theme.customized.textTransforms.buttons};
  transition: all 0.2s;
  width: 100%;
  z-index: 1;

  ${({ theme }) =>
    theme.customized.colors.productCardButton === ProductCardButtonOption.tinted
      ? tintedButtonStyles
      : solidButtonStyles}

  &:hover {
    ${({ theme }) =>
      theme.customized.colors.productCardButton === ProductCardButtonOption.tinted
        ? solidButtonStyles
        : solidButtonHoverStyles}
  }
`;

const Button = styled.button`
  ${buttonStyles}
`;

const EmbeddedButton = styled(EmbeddedExternalLink)`
  ${buttonStyles}
`;
